// ============================================================
// Notice & Announcement Center — Types & Config
// ============================================================

// ─── Enums ───────────────────────────────────────────────────
export type NoticeType =
  | 'emergency' | 'promotion' | 'fare_update' | 'route_notice'
  | 'maintenance' | 'app_update' | 'general';

export type NoticePriority = 'critical' | 'high' | 'normal';

export type NoticeStatus = 'draft' | 'published' | 'archived';

// ─── Notice interface ────────────────────────────────────────
export interface Notice {
  id: string;
  title: string;
  short_message: string;
  summary: string;
  body: string;
  type: NoticeType;
  priority: NoticePriority;
  status: NoticeStatus;
  is_pinned: boolean;
  is_dismissible: boolean;
  show_in_top_bar: boolean;
  show_on_homepage: boolean;
  cta_label: string | null;
  cta_url: string | null;
  starts_at: string;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined relations (optional)
  routes?: { id: string; origin: string; destination: string }[];
  counters?: { id: string; name: string; district: string }[];
}

// ─── Form data for create/edit ───────────────────────────────
export interface NoticeFormData {
  title: string;
  short_message: string;
  summary: string;
  body: string;
  type: NoticeType;
  priority: NoticePriority;
  status: NoticeStatus;
  is_pinned: boolean;
  is_dismissible: boolean;
  show_in_top_bar: boolean;
  show_on_homepage: boolean;
  cta_label: string;
  cta_url: string;
  starts_at: string;
  expires_at: string;
  route_ids: string[];
  counter_ids: string[];
}

export const emptyNoticeForm: NoticeFormData = {
  title: '',
  short_message: '',
  summary: '',
  body: '',
  type: 'general',
  priority: 'normal',
  status: 'draft',
  is_pinned: false,
  is_dismissible: true,
  show_in_top_bar: false,
  show_on_homepage: false,
  cta_label: '',
  cta_url: '',
  starts_at: '',
  expires_at: '',
  route_ids: [],
  counter_ids: [],
};

// ─── Filter params ───────────────────────────────────────────
export interface NoticeFilters {
  search?: string;
  type?: NoticeType | 'all';
  priority?: NoticePriority | 'all';
  status?: 'active' | 'archived' | 'all';
}

// ─── Config maps ─────────────────────────────────────────────
export const noticeTypes: NoticeType[] = [
  'emergency', 'promotion', 'fare_update', 'route_notice',
  'maintenance', 'app_update', 'general',
];

export const noticeTypeConfig: Record<NoticeType, {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
}> = {
  emergency:    { label: 'Emergency',    color: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/30',     icon: '🚨' },
  promotion:    { label: 'Promotion',    color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: '🎉' },
  fare_update:  { label: 'Fare Update',  color: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   icon: '💰' },
  route_notice: { label: 'Route Notice', color: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    icon: '🛣️' },
  maintenance:  { label: 'Maintenance',  color: 'text-slate-400',   bg: 'bg-slate-500/10',   border: 'border-slate-500/30',   icon: '🔧' },
  app_update:   { label: 'App Update',   color: 'text-violet-400',  bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  icon: '📱' },
  general:      { label: 'General',      color: 'text-zinc-400',    bg: 'bg-zinc-500/10',    border: 'border-zinc-500/30',    icon: 'ℹ️' },
};

export const noticePriorityConfig: Record<NoticePriority, {
  label: string;
  color: string;
  bg: string;
  dot: string;
}> = {
  critical: { label: 'Critical', color: 'text-red-400',          bg: 'bg-red-500/15',    dot: 'bg-red-500' },
  high:     { label: 'High',     color: 'text-orange-400',       bg: 'bg-orange-500/15', dot: 'bg-orange-500' },
  normal:   { label: 'Normal',   color: 'text-muted-foreground', bg: 'bg-secondary',     dot: 'bg-muted-foreground' },
};

/** Passenger-facing Bengali labels; notice content itself remains author-provided. */
export const noticeTypeBanglaLabels: Record<NoticeType, string> = {
  emergency: 'জরুরি',
  promotion: 'বিশেষ সুবিধা',
  fare_update: 'ভাড়া পরিবর্তন',
  route_notice: 'যাত্রাপথের সংবাদ',
  maintenance: 'রক্ষণাবেক্ষণ',
  app_update: 'অ্যাপের নতুন সংস্করণ',
  general: 'সাধারণ',
};

export const noticePriorityBanglaLabels: Record<NoticePriority, string> = {
  critical: 'অতি জরুরি',
  high: 'জরুরি',
  normal: 'সাধারণ',
};

// ─── Helpers ─────────────────────────────────────────────────
/** Check if a notice is currently active (published, started, not expired) */
export function isNoticeActive(n: Notice): boolean {
  if (n.status !== 'published') return false;
  const now = new Date();
  const start = new Date(n.starts_at);
  if (start > now) return false;
  if (n.expires_at && new Date(n.expires_at) < now) return false;
  return true;
}

/** Sort notices: pinned first → critical → high → normal → newest */
export function sortNotices(notices: Notice[]): Notice[] {
  const priorityOrder: Record<NoticePriority, number> = { critical: 0, high: 1, normal: 2 };
  return [...notices].sort((a, b) => {
    if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
    if (a.priority !== b.priority) return priorityOrder[a.priority] - priorityOrder[b.priority];
    return new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime();
  });
}

/** Format date for display in Bangladesh local time */
export function formatNoticeDate(dateStr: string, locale = 'en-GB'): string {
  return new Date(dateStr).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Dhaka',
  });
}

/** Format date + time */
export function formatNoticeDateTime(dateStr: string, locale = 'en-GB'): string {
  const lang = locale.startsWith('bn') ? 'bn' : 'en';
  return `${formatNoticeDate(dateStr, locale)}, ${formatTime12(dateStr, lang)}`;
}

/** Convert local datetime-local input to ISO string with BST offset */
export function localInputToISO(val: string): string {
  if (!val) return '';
  // datetime-local gives "YYYY-MM-DDTHH:mm" — treat as BST (UTC+6)
  return val + ':00+06:00';
}

/** Convert ISO to datetime-local input value */
export function isoToLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  // Convert to BST
  const bst = new Date(d.getTime() + 6 * 60 * 60 * 1000);
  const y = bst.getUTCFullYear();
  const m = String(bst.getUTCMonth() + 1).padStart(2, '0');
  const day = String(bst.getUTCDate()).padStart(2, '0');
  const h = String(bst.getUTCHours()).padStart(2, '0');
  const min = String(bst.getUTCMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}
import { formatTime12 } from '@/lib/formatters';
